import classNames from 'classnames/bind';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';

import ErrorFocus from 'uikit/ErrorFocus';
import Button from 'uikit/Button';
import Icon from 'uikit/Icon';
import Menu, { MenuItem } from 'uikit/Menu';
import Modal from 'uikit/Modal';
import SubPage from 'uikit/SubPage';

import QuestionTypeCard from './QuestionTypeCard';
import QuestionGrade from './QuestionGrade';
import styles from './QuestionEdit.module.scss';

const cx = classNames.bind(styles);

const QuestionEdit = ({
  askCloseValidation,
  canSelectChapter,
  course,
  editorComponent: Editor,
  editorStatus,
  handleClose,
  handleCloseUndo,
  handleDelete,
  handleDeleteModalClose,
  handleDeleteModalOpen,
  handleGradeModalClose,
  handleGradeModalOpen,
  handleQuestionsImport,
  handleRecreate,
  handleSave,
  handleTypeSelect,
  initialValues,
  isDeleteModalOpen,
  isGradeModalOpen,
  isModalOpen,
  questionInfo,
  questionsInfo,
  t,
  validationSchema,
}) => (
  <Formik
    enableReinitialize
    initialValues={initialValues}
    onSubmit={
      questionInfo?.type === 'PDFOrPPT' ? handleQuestionsImport : handleSave
    }
    validationSchema={validationSchema}
    validateOnBlur={false}
    validateOnChange={false}
  >
    {({
      dirty,
      isSubmitting,
      handleSubmit,
      setErrors,
      setFieldValue,
      setValues,
      submitForm,
      validateForm,
      values,
    }) => (
      <>
        <SubPage
          header={
            Editor &&
            (editorStatus === 'question-create' ? (
              <label className={cx('select-type')} htmlFor="type">
                {questionInfo ? (
                  <img alt="" className={cx('icon')} src={questionInfo.icon} />
                ) : (
                  <Icon className={cx('icon')} name="question" />
                )}
                <select
                  aria-label={t('Course/Shared/QuestionEdit/aria-type')}
                  id="type"
                  name="type"
                  onChange={handleTypeSelect}
                  value={questionInfo ? questionInfo.type : ''}
                >
                  <option disabled value="">
                    {t('Course/Shared/QuestionEdit/type-placeholder')}
                  </option>
                  {questionsInfo.all.map((info) => (
                    <option key={info.type} value={info.type}>
                      {t(info.name)}
                    </option>
                  ))}
                </select>
                <div className={cx('arrow')}>
                  <Icon name="chevron-down" />
                </div>
              </label>
            ) : (
              <div className={cx('select-type')}>
                <img alt="" className={cx('icon')} src={questionInfo.icon} />
                <div className={cx('select-type-text')}>
                  {t(questionInfo.name)}
                </div>
              </div>
            ))
          }
          isOpen={isModalOpen}
          onExit={() => handleClose(setValues, dirty)}
          headerSize="small"
        >
          <div className={cx('subpage')}>
            {Editor ? (
              <Form className={cx('form')}>
                <Editor course={course} />
                <div className={cx('actions')}>
                  {editorStatus !== 'question-approve' &&
                  questionInfo.type !== 'PDFOrPPT' ? (
                    <Button
                      className={cx('action')}
                      icon="redo"
                      isLoading={isSubmitting}
                      onClick={handleRecreate(
                        setErrors,
                        setValues,
                        submitForm,
                        validateForm,
                      )}
                      intent="white"
                      size="large"
                    >
                      {editorStatus === 'question-propose'
                        ? t('Course/Shared/QuestionEdit/repropose-question', {
                            name: t(questionInfo.name),
                          })
                        : t('Course/Shared/QuestionEdit/recreate-question', {
                            name: t(questionInfo.name),
                          })}
                    </Button>
                  ) : questionInfo?.type !== 'PDFOrPPT' ? (
                    <Button
                      className={cx('action')}
                      icon="trash-alt"
                      isLoading={isSubmitting}
                      onClick={() => {
                        if (editorStatus === 'question-approve') {
                          handleDeleteModalOpen();
                        } else {
                          handleDelete();
                        }
                      }}
                      intent="white"
                      size="large"
                    >
                      {t('Course/Shared/QuestionEdit/delete-question')}
                    </Button>
                  ) : null}
                  <div className={cx('actions-right')}>
                    {questionInfo?.type !== 'PDFOrPPT' &&
                      editorStatus !== 'question-approve' && (
                        <Button
                          className={cx('action', 'preview-button')}
                          icon="eye"
                          intent="white"
                          onClick={() => {
                            setFieldValue('wantPreview', true);
                            handleSubmit();
                          }}
                          size="large"
                        >
                          {t('Course/Shared/QuestionEdit/preview-question')}
                        </Button>
                      )}
                    <div className={cx('split-button-container')}>
                      <Button
                        className={cx('action', {
                          'split-button': canSelectChapter,
                        })}
                        data-testid="save-question-button"
                        icon="check"
                        intent="primary"
                        isLoading={isSubmitting}
                        onClick={
                          editorStatus === 'question-approve'
                            ? () => handleGradeModalOpen()
                            : () => {
                                setFieldValue('wantPreview', false);
                                handleSubmit();
                              }
                        }
                        size="large"
                      >
                        {questionInfo.type === 'PDFOrPPT'
                          ? t('Course/Shared/QuestionEdit/confirm')
                          : editorStatus === 'question-approve'
                          ? t('Course/Shared/QuestionEdit/approve-question')
                          : editorStatus === 'question-propose'
                          ? t('Course/Shared/QuestionEdit/propose-question')
                          : t('Course/Shared/QuestionEdit/save-question')}
                      </Button>
                      {canSelectChapter && (
                        <Menu
                          placement="bottom"
                          toggle={
                            <Button
                              className={cx('save-in-chapter-button')}
                              icon="chevron-down"
                              intent="white"
                              size="large"
                            />
                          }
                          trigger="mouseenter focus"
                        >
                          <div className={cx('save-in-chapter-title')}>
                            {t('Course/Shared/QuestionEdit/save-question-in')}
                          </div>
                          {course.chapters.map((chapter) => (
                            <MenuItem
                              key={chapter.id}
                              onClick={() => {
                                setFieldValue('chapterId', chapter.id);
                                if (editorStatus === 'question-approve') {
                                  handleGradeModalOpen();
                                } else {
                                  handleSubmit();
                                }
                              }}
                            >
                              {chapter.title}
                            </MenuItem>
                          ))}
                        </Menu>
                      )}
                    </div>
                  </div>
                  <ErrorFocus />
                </div>
                <QuestionGrade
                  handleModalClose={handleGradeModalClose}
                  handleSubmit={handleSubmit}
                  isOpen={isGradeModalOpen}
                  isSubmitting={isSubmitting}
                  setFieldValue={setFieldValue}
                />
                <QuestionGrade
                  handleModalClose={handleDeleteModalClose}
                  handleSubmit={() => handleDelete(values)}
                  isOpen={isDeleteModalOpen}
                  isSubmitting={isSubmitting}
                  setFieldValue={setFieldValue}
                />
              </Form>
            ) : (
              <div className={cx('types')}>
                <div>
                  <h5 className={cx('category-title', 'informative-title')}>
                    {t('Course/Shared/QuestionEdit/informative-questions')}
                  </h5>
                  <div className={cx('cards', 'informative-cards')}>
                    {questionsInfo.informativeQuestions.map((info) => (
                      <QuestionTypeCard
                        className={cx('type')}
                        info={info}
                        key={info.type}
                      />
                    ))}
                  </div>
                  <h5 className={cx('category-title')}>
                    {t('Course/Shared/QuestionEdit/questions')}
                  </h5>
                  <div className={cx('cards')}>
                    {questionsInfo.questions.map((info) => (
                      <div key={info.type}>
                        {(editorStatus !== 'question-propose' ||
                          (editorStatus === 'question-propose' &&
                            info.type !== 'PDFOrPPT')) && (
                          <QuestionTypeCard
                            className={cx('type')}
                            info={info}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </SubPage>
        {askCloseValidation && (
          <Modal isOpen onExit={handleCloseUndo}>
            <div className={cx('modal')}>
              <h1 className={cx('title')}>
                {t('Course/Shared/QuestionEdit/ask-close')}
              </h1>
              <Button
                className={cx('action')}
                onClick={handleCloseUndo}
                size="large"
              >
                {t('Course/Shared/QuestionEdit/undo-close')}
              </Button>
              <Button
                className={cx('action')}
                intent="primary"
                onClick={() => handleClose(setValues)}
                size="large"
              >
                {t('Course/Shared/QuestionEdit/force-close')}
              </Button>
            </div>
          </Modal>
        )}
      </>
    )}
  </Formik>
);

QuestionEdit.propTypes = {
  askCloseValidation: PropTypes.bool.isRequired,
  canSelectChapter: PropTypes.bool.isRequired,
  course: PropTypes.object.isRequired,
  editorComponent: PropTypes.elementType,
  editorStatus: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  handleCloseUndo: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleDeleteModalClose: PropTypes.func.isRequired,
  handleDeleteModalOpen: PropTypes.func.isRequired,
  handleGradeModalClose: PropTypes.func.isRequired,
  handleGradeModalOpen: PropTypes.func.isRequired,
  handleQuestionsImport: PropTypes.func.isRequired,
  handleRecreate: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleTypeSelect: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  isDeleteModalOpen: PropTypes.bool.isRequired,
  isGradeModalOpen: PropTypes.bool.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  questionInfo: PropTypes.object,
  questionsInfo: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  validationSchema: PropTypes.object,
};

QuestionEdit.defaultProps = {
  editorComponent: undefined,
  editorStatus: null,
  questionInfo: undefined,
  validationSchema: undefined,
};

export default QuestionEdit;
